/**
 * ============================================================
 * RISE SKIN CONCIERGE
 * Quiz Intelligence + Client Profile — Streamlined v1.2
 * ============================================================
 *
 * This file:
 * - Defines quiz questions
 * - Stores answers
 * - Builds the client skin profile
 * - Controls conditional question visibility
 *
 * This file DOES NOT render the quiz UI.
 * UI belongs in rise-skin-quiz-ui.js.
 * Product recommendations belong in rise-skin-engine.js.
 * ============================================================
 */

window.RISE_SKIN = window.RISE_SKIN || {};

// ============================================================
// QUIZ CONFIG
// ============================================================

window.RISE_SKIN.QUIZ_VERSION = "1.2";

window.RISE_SKIN.quizConfig = {
  maxPrimaryConcerns: 3,
  allowBack: true,
  saveProgress: true
};


// ============================================================
// EMPTY CLIENT PROFILE
// ============================================================

window.RISE_SKIN.createEmptyProfile = function () {
  return {
    quizVersion: window.RISE_SKIN.QUIZ_VERSION,

    // Core
    concerns: [],
    ageRange: null,

    // Skin behavior
    skinBehavior: null,
    sensitivity: null,
    barrierStatus: null,

    // Concern detail
    acneSeverity: null,
    pigmentType: null,
    rednessPattern: null,
    agingConcerns: [],
    eyeConcerns: [],
    bodyConcerns: [],

    // Current routine / tolerance
    currentActives: [],
    retinoidExperience: null,

    // SPF personalization
    spfPreference: [],

    // Safety
    safetyFlags: [],
    pregnant: false,
    breastfeeding: false,
    prescriptionRetinoid: false,
    recentProcedure: false,

    // Compatibility defaults for removed questions.
    // These remain so downstream code can safely read them.
    sunExposure: null,
    spfConsistency: null,
    routineCommitment: null,
    treatmentInterest: null,
    treatmentFit: null,
    eventTiming: null,

    // Metadata
    completed: false,
    startedAt: new Date().toISOString(),
    completedAt: null
  };
};


// ============================================================
// ACTIVE QUIZ STATE
// ============================================================

window.RISE_SKIN.quizState = {
  currentQuestionIndex: 0,
  answers: {},
  profile: window.RISE_SKIN.createEmptyProfile()
};


// ============================================================
// QUESTION DEFINITIONS
// ============================================================

window.RISE_SKIN.QUIZ_QUESTIONS = [

  // ----------------------------------------------------------
  // 1 — PRIMARY GOALS
  // ----------------------------------------------------------
  {
    id: "primary_concerns",
    number: 1,
    profileKey: "concerns",
    type: "ranked-multi-select",
    eyebrow: "Let's start with your skin goals",
    question: "What would you most love to improve about your skin?",
    helper: "Choose up to 3. Select your biggest priority first.",
    maxSelections: 3,
    options: [
      { id: "acne", label: "Breakouts + acne", concernKey: "acne" },
      { id: "pigment", label: "Dark spots + uneven tone", concernKey: "pigment" },
      { id: "redness", label: "Redness + sensitivity", concernKey: "redness" },
      { id: "fine_lines", label: "Fine lines + wrinkles", concernKey: "fineLines" },
      { id: "laxity", label: "Firmness + skin laxity", concernKey: "laxity" },
      { id: "texture", label: "Texture + roughness", concernKey: "texture" },
      { id: "pores", label: "Visible pores", concernKey: "pores" },
      { id: "dullness", label: "Dullness + lack of glow", concernKey: "dullness" },
      { id: "dehydration", label: "Dryness + dehydration", concernKey: "dehydration" },
      { id: "under_eye", label: "Under-eye concerns", concernKey: "underEye" },
      { id: "prevention", label: "Preventative skin health", concernKey: "prevention" }
    ]
  },

  // ----------------------------------------------------------
  // 2 — AGE
  // ----------------------------------------------------------
  {
    id: "age_range",
    number: 2,
    profileKey: "ageRange",
    type: "single-select",
    eyebrow: "A little about you",
    question: "What's your age range?",
    helper: "Age gives us context, but your actual skin concerns always come first.",
    options: [
      { id: "under_18", label: "Under 18", value: "under_18" },
      { id: "18_24", label: "18–24", value: "18_24" },
      { id: "25_34", label: "25–34", value: "25_34" },
      { id: "35_44", label: "35–44", value: "35_44" },
      { id: "45_54", label: "45–54", value: "45_54" },
      { id: "55_64", label: "55–64", value: "55_64" },
      { id: "65_plus", label: "65+", value: "65_plus" }
    ]
  },

  // ----------------------------------------------------------
  // 3 — BASELINE SKIN BEHAVIOR
  // ----------------------------------------------------------
  {
    id: "skin_behavior",
    number: 3,
    profileKey: "skinBehavior",
    type: "single-select",
    eyebrow: "Your skin day-to-day",
    question: "By midday, how does your skin usually feel?",
    helper: "Think about your skin on an average day without changing your routine.",
    options: [
      { id: "tight_dry", label: "Tight or dry", value: "dry" },
      { id: "comfortable", label: "Pretty comfortable + balanced", value: "balanced" },
      { id: "tzone_oily", label: "Oily through my T-zone", value: "combination" },
      { id: "oily", label: "Oily or shiny all over", value: "oily" },
      { id: "changes", label: "It changes constantly", value: "variable" },
      { id: "unsure", label: "Honestly, I'm not sure", value: "unknown" }
    ]
  },

  // ----------------------------------------------------------
  // 4 — SENSITIVITY
  // ----------------------------------------------------------
  {
    id: "sensitivity",
    number: 4,
    profileKey: "sensitivity",
    type: "single-select",
    eyebrow: "How reactive is your skin?",
    question: "How does your skin usually handle new products or active ingredients?",
    helper: "This helps us decide how aggressive—or gentle—your routine should be.",
    options: [
      { id: "resilient", label: "My skin can handle almost anything", value: "resilient" },
      { id: "mostly_fine", label: "Usually fine, with occasional irritation", value: "normal" },
      { id: "somewhat_sensitive", label: "I have to introduce products carefully", value: "sensitive" },
      { id: "very_sensitive", label: "My skin reacts very easily", value: "very_sensitive" },
      { id: "unknown", label: "I'm not really sure", value: "unknown" }
    ]
  },

  // ----------------------------------------------------------
  // 5 — BARRIER STATUS
  // ----------------------------------------------------------
  {
    id: "barrier_status",
    number: 5,
    profileKey: "barrierStatus",
    type: "single-select",
    eyebrow: "One important skin check",
    question: "Does your skin currently feel irritated, compromised, or unusually reactive?",
    helper: "Think burning, stinging, peeling, rawness, or products suddenly bothering you.",
    options: [
      { id: "no", label: "No — my skin feels normal", value: "healthy" },
      { id: "mild", label: "A little dry, flaky, or irritated", value: "mild" },
      { id: "yes", label: "Yes — burning, stinging, peeling, or very reactive", value: "impaired" },
      { id: "post_procedure", label: "I'm healing from a recent treatment", value: "post_procedure" },
      { id: "unsure", label: "I'm not sure", value: "unknown" }
    ]
  },

  // ----------------------------------------------------------
  // 6 — ACNE DETAIL — ONLY WHEN ACNE IS A GOAL
  // ----------------------------------------------------------
  {
    id: "acne_frequency",
    number: 6,
    profileKey: "acneSeverity",
    type: "single-select",
    eyebrow: "Let's talk breakouts",
    question: "How often are breakouts part of the picture?",
    helper: "This helps us choose the right level of acne correction without over-treating your skin.",
    showWhen: {
      any: [
        { questionId: "primary_concerns", includes: "acne" }
      ]
    },
    options: [
      { id: "rare", label: "Rarely or never", value: "rare" },
      { id: "occasional", label: "A few occasionally", value: "occasional" },
      { id: "congested", label: "Regular clogged pores or blackheads", value: "congested" },
      { id: "inflamed", label: "Regular inflamed pimples", value: "inflamed" },
      { id: "cystic", label: "Deep, painful, or cystic breakouts", value: "cystic" }
    ]
  },

  // ----------------------------------------------------------
  // 7 — PIGMENT DETAIL — ONLY WHEN PIGMENT IS A GOAL
  // ----------------------------------------------------------
  {
    id: "pigment_pattern",
    number: 7,
    profileKey: "pigmentType",
    type: "single-select",
    eyebrow: "Let's look closer at discoloration",
    question: "Which best describes discoloration for you?",
    helper: "This helps distinguish general sun-related pigment from post-breakout marks or more stubborn patterns.",
    showWhen: {
      any: [
        { questionId: "primary_concerns", includes: "pigment" }
      ]
    },
    options: [
      { id: "none", label: "Not really a concern", value: "none" },
      { id: "light_sun", label: "A few freckles or sun spots", value: "light_sun" },
      { id: "sun_damage", label: "Noticeable brown spots or uneven tone", value: "sun_damage" },
      { id: "post_acne", label: "Dark marks left after breakouts", value: "post_acne" },
      { id: "melasma", label: "Patchy or stubborn pigmentation", value: "melasma_pattern" }
    ]
  },

  // ----------------------------------------------------------
  // 8 — REDNESS DETAIL — ONLY WHEN REDNESS IS A GOAL
  // ----------------------------------------------------------
  {
    id: "redness_pattern",
    number: 8,
    profileKey: "rednessPattern",
    type: "single-select",
    eyebrow: "Let's look closer at redness",
    question: "Which sounds most like your skin?",
    helper: "This helps us distinguish occasional flushing from persistent, vascular, or reactive redness.",
    showWhen: {
      any: [
        { questionId: "primary_concerns", includes: "redness" }
      ]
    },
    options: [
      { id: "none", label: "Redness isn't really a concern", value: "none" },
      { id: "flushes", label: "I flush sometimes, but it goes away", value: "flushing" },
      { id: "persistent", label: "I have persistent or general redness", value: "persistent" },
      { id: "vessels", label: "I can see tiny vessels or broken capillaries", value: "vascular" },
      { id: "reactive", label: "Redness comes with bumps, sensitivity, or irritation", value: "reactive" }
    ]
  },

  // ----------------------------------------------------------
  // 9 — AGING DETAIL — ONLY WHEN RELEVANT
  // ----------------------------------------------------------
  {
    id: "aging_concerns",
    number: 9,
    profileKey: "agingConcerns",
    type: "multi-select",
    eyebrow: "Aging looks different on everyone",
    question: "Which changes are you noticing?",
    helper: "Choose all that apply. This helps separate surface skin concerns from expression lines or structural changes.",
    showWhen: {
      any: [
        { questionId: "primary_concerns", includes: "fine_lines" },
        { questionId: "primary_concerns", includes: "laxity" },
        { questionId: "primary_concerns", includes: "prevention" }
      ]
    },
    options: [
      { id: "prevention", label: "None yet—I'm focused on prevention", value: "prevention" },
      { id: "early_lines", label: "Fine lines are starting", value: "early_lines" },
      { id: "dynamic_lines", label: "Lines mostly show when I make expressions", value: "dynamic_lines" },
      { id: "resting_lines", label: "Lines are visible even when my face is relaxed", value: "resting_lines" },
      { id: "laxity", label: "Loss of firmness or sagging", value: "laxity" },
      { id: "volume_loss", label: "Hollowing or volume loss", value: "volume_loss" }
    ]
  },

  // ----------------------------------------------------------
  // 10 — UNDER-EYE DETAIL — ONLY WHEN RELEVANT
  // ----------------------------------------------------------
  {
    id: "eye_concerns",
    number: 10,
    profileKey: "eyeConcerns",
    type: "multi-select",
    eyebrow: "Let's look closer at your eye area",
    question: "What would you most like to improve around your eyes?",
    helper: "Choose anything that genuinely bothers you.",
    showWhen: {
      any: [
        { questionId: "primary_concerns", includes: "under_eye" },
        { questionId: "aging_concerns", includes: "volume_loss" }
      ]
    },
    options: [
      { id: "none", label: "Nothing specific", value: "none" },
      { id: "prevention", label: "Prevention", value: "prevention" },
      { id: "fine_lines", label: "Fine lines or crepey skin", value: "fine_lines" },
      { id: "puffiness", label: "Puffiness or under-eye bags", value: "puffiness" },
      { id: "dark_circles", label: "Dark circles or shadowing", value: "dark_circles" },
      { id: "hollowing", label: "Hollowing or a sunken appearance", value: "hollowing" }
    ]
  },

  // ----------------------------------------------------------
  // 11 — CURRENT ACTIVES
  // ----------------------------------------------------------
  {
    id: "current_actives",
    number: 11,
    profileKey: "currentActives",
    type: "multi-select",
    eyebrow: "A quick look at your current routine",
    question: "Which stronger skincare ingredients are you currently using or comfortable with?",
    helper: "Choose anything that applies. If you're not sure, that's totally fine.",
    options: [
      { id: "retinoid", label: "Retinol, retinal, or another retinoid", value: "retinoid" },
      { id: "exfoliating_acids", label: "Exfoliating acids like glycolic, lactic, mandelic, or salicylic acid", value: "exfoliating_acids" },
      { id: "benzoyl_peroxide", label: "Benzoyl peroxide or acne actives", value: "benzoyl_peroxide" },
      { id: "prescription_skin_medication", label: "Prescription acne or skin medication", value: "prescription_skin_medication" },
      { id: "none", label: "None of these", value: "none" },
      { id: "unsure", label: "I'm not sure", value: "unsure" }
    ]
  },

  // ----------------------------------------------------------
  // 12 — RETINOID EXPERIENCE — ONLY WHEN RETINOID MAY MATTER
  // ----------------------------------------------------------
  {
    id: "retinoid_experience",
    number: 12,
    profileKey: "retinoidExperience",
    type: "single-select",
    eyebrow: "One quick retinoid question",
    question: "How does your skin handle retinoids?",
    helper: "This helps us choose an appropriate retinoid and avoid over-correcting sensitive skin.",
    showWhen: {
      any: [
        { questionId: "current_actives", includes: "retinoid" },
        { questionId: "primary_concerns", includes: "fine_lines" },
        { questionId: "primary_concerns", includes: "texture" },
        { questionId: "primary_concerns", includes: "pores" },
        { questionId: "primary_concerns", includes: "acne" },
        { questionId: "primary_concerns", includes: "prevention" }
      ]
    },
    options: [
      { id: "never_used", label: "I've never used one", value: "never_used" },
      { id: "tolerates_well", label: "I use one and tolerate it well", value: "tolerates_well" },
      { id: "some_irritation", label: "I can use one, but I get irritated easily", value: "some_irritation" },
      { id: "stopped_irritation", label: "I've stopped one because of irritation", value: "stopped_irritation" },
      { id: "unsure", label: "I'm not sure", value: "unsure" }
    ]
  },

  // ----------------------------------------------------------
  // 13 — SPF PREFERENCE
  // ----------------------------------------------------------
  {
    id: "spf_preference",
    number: 13,
    profileKey: "spfPreference",
    type: "multi-select",
    maxSelections: 2,
    eyebrow: "Your best SPF is the one you'll actually wear",
    question: "What kind of sunscreen would you enjoy wearing?",
    helper: "Choose up to 2.",
    options: [
      { id: "tinted", label: "Tinted with some coverage", value: "tinted" },
      { id: "sheer", label: "Sheer or basically invisible", value: "sheer" },
      { id: "glowy", label: "Glowy or dewy", value: "glowy" },
      { id: "matte", label: "Matte or natural finish", value: "matte" },
      { id: "reapplication", label: "Something especially easy to reapply", value: "reapplication" },
      { id: "no_preference", label: "No real preference", value: "no_preference" }
    ]
  },

  // ----------------------------------------------------------
  // 14 — BODY CONCERNS
  // ----------------------------------------------------------
  {
    id: "body_concerns",
    number: 14,
    profileKey: "bodyConcerns",
    type: "multi-select",
    eyebrow: "Beyond the face",
    question: "Would you like recommendations for the skin on your body, too?",
    helper: "Select all that apply.",
    options: [
      { id: "crepey_firmness", label: "Crepey skin / loss of firmness", value: "crepey_firmness" },
      { id: "sun_damage", label: "Sun damage / discoloration", value: "sun_damage" },
      { id: "body_breakouts", label: "Body breakouts", value: "body_breakouts" },
      { id: "stretch_marks", label: "Stretch marks / skin quality", value: "stretch_marks" },
      { id: "prevention", label: "I want preventative body care", value: "prevention" },
      { id: "none", label: "No body concerns right now", value: "none" }
    ]
  },

  // ----------------------------------------------------------
  // 15 — SAFETY SCREEN
  // ----------------------------------------------------------
  {
    id: "safety_screen",
    number: 15,
    profileKey: "safetyFlags",
    type: "multi-select",
    eyebrow: "One quick safety check",
    question: "Do any of these currently apply to you?",
    helper: "These answers help us avoid products or treatments that need more careful review.",
    options: [
      { id: "pregnant_ttc", label: "Pregnant or trying to conceive", value: "pregnant_ttc" },
      { id: "breastfeeding", label: "Breastfeeding", value: "breastfeeding" },
      { id: "dermatology_care", label: "I'm under active dermatology care", value: "dermatology_care" },
      { id: "prescription_retinoid", label: "I'm using a prescription retinoid or acne medication", value: "prescription_retinoid" },
      { id: "recent_reaction", label: "I've recently had a significant skin reaction or procedure", value: "recent_reaction" },
      { id: "none", label: "None of these apply", value: "none" }
    ]
  }
];


// ============================================================
// BASIC QUIZ HELPERS
// ============================================================

window.RISE_SKIN.getQuizQuestions = function () {
  return window.RISE_SKIN.QUIZ_QUESTIONS || [];
};

window.RISE_SKIN.getQuestionById = function (questionId) {
  return (
    (window.RISE_SKIN.QUIZ_QUESTIONS || []).find(
      (question) => question.id === questionId
    ) || null
  );
};

window.RISE_SKIN.resetQuiz = function () {
  window.RISE_SKIN.quizState = {
    currentQuestionIndex: 0,
    answers: {},
    profile: window.RISE_SKIN.createEmptyProfile()
  };

  if (window.RISE_DEBUG) {
    console.log(
      "RISE SKIN CONCIERGE — quiz reset",
      window.RISE_SKIN.quizState
    );
  }

  return window.RISE_SKIN.quizState;
};


// ============================================================
// QUESTION VISIBILITY ENGINE
// ============================================================

window.RISE_SKIN.answerMatchesCondition = function (
  answer,
  condition
) {
  if (
    condition.includes &&
    Array.isArray(answer)
  ) {
    return answer.some((item) => {
      if (typeof item === "string") {
        return item === condition.includes;
      }

      if (item && typeof item === "object") {
        return (
          item.id === condition.includes ||
          item.value === condition.includes ||
          item.concernKey === condition.includes
        );
      }

      return false;
    });
  }

  if (
    condition.includes &&
    typeof answer === "string"
  ) {
    return answer === condition.includes;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      condition,
      "equals"
    )
  ) {
    return answer === condition.equals;
  }

  return false;
};

window.RISE_SKIN.isQuestionVisible = function (question) {
  if (!question.showWhen) {
    return true;
  }

  const answers =
    window.RISE_SKIN.quizState.answers || {};

  if (Array.isArray(question.showWhen.any)) {
    return question.showWhen.any.some((condition) =>
      window.RISE_SKIN.answerMatchesCondition(
        answers[condition.questionId],
        condition
      )
    );
  }

  if (Array.isArray(question.showWhen.all)) {
    return question.showWhen.all.every((condition) =>
      window.RISE_SKIN.answerMatchesCondition(
        answers[condition.questionId],
        condition
      )
    );
  }

  return true;
};

window.RISE_SKIN.getVisibleQuizQuestions = function () {
  return (
    window.RISE_SKIN.QUIZ_QUESTIONS || []
  ).filter((question) =>
    window.RISE_SKIN.isQuestionVisible(question)
  );
};


// ============================================================
// QUIZ ANSWER NORMALIZATION
// ============================================================

window.RISE_SKIN.normalizeAnswer = function (
  question,
  rawAnswer
) {
  if (!question) {
    return rawAnswer;
  }

  if (question.type === "single-select") {
    const option =
      (question.options || []).find(
        (item) =>
          item.id === rawAnswer ||
          item.value === rawAnswer
      );

    return option
      ? option.value
      : rawAnswer;
  }

  if (
    question.type === "multi-select" ||
    question.type === "ranked-multi-select"
  ) {
    if (!Array.isArray(rawAnswer)) {
      return [];
    }

    return rawAnswer.map((answer) => {
      const option =
        (question.options || []).find(
          (item) =>
            item.id === answer ||
            item.value === answer
        );

      return option
        ? option.value || option.id
        : answer;
    });
  }

  return rawAnswer;
};


// ============================================================
// SAVE AN ANSWER
// ============================================================

window.RISE_SKIN.setQuizAnswer = function (
  questionId,
  rawAnswer
) {
  const question =
    window.RISE_SKIN.getQuestionById(
      questionId
    );

  if (!question) {
    console.error(
      `RISE SKIN: Question not found: ${questionId}`
    );
    return null;
  }

  const normalized =
    window.RISE_SKIN.normalizeAnswer(
      question,
      rawAnswer
    );

  window.RISE_SKIN.quizState.answers[
    questionId
  ] = normalized;

  if (question.profileKey) {
    window.RISE_SKIN.quizState.profile[
      question.profileKey
    ] = normalized;
  }

  if (questionId === "safety_screen") {
    const flags =
      Array.isArray(normalized)
        ? normalized
        : [];

    window.RISE_SKIN.quizState.profile.pregnant =
      flags.includes("pregnant_ttc");

    window.RISE_SKIN.quizState.profile.breastfeeding =
      flags.includes("breastfeeding");

    window.RISE_SKIN.quizState.profile.prescriptionRetinoid =
      flags.includes("prescription_retinoid");

    window.RISE_SKIN.quizState.profile.recentProcedure =
      flags.includes("recent_reaction");
  }

  if (window.RISE_DEBUG) {
    console.log(
      `RISE SKIN — answer saved: ${questionId}`,
      normalized
    );
  }

  return normalized;
};


// ============================================================
// COMPLETE QUIZ
// ============================================================

window.RISE_SKIN.completeQuiz = function () {
  const profile =
    window.RISE_SKIN.quizState.profile;

  profile.completed = true;
  profile.completedAt =
    new Date().toISOString();

  if (window.RISE_DEBUG) {
    console.group(
      "RISE SKIN CONCIERGE — QUIZ COMPLETE"
    );
    console.log(
      "Answers:",
      window.RISE_SKIN.quizState.answers
    );
    console.log("Profile:", profile);
    console.groupEnd();
  }

  return profile;
};


// ============================================================
// VALIDATION
// ============================================================

window.RISE_SKIN.validateQuiz = function () {
  const questions =
    window.RISE_SKIN.QUIZ_QUESTIONS || [];

  const errors = [];
  const ids = new Set();

  questions.forEach((question, index) => {
    const label =
      question.id || `Question ${index + 1}`;

    if (!question.id) {
      errors.push(`${label}: missing id`);
    }

    if (question.id && ids.has(question.id)) {
      errors.push(`${label}: duplicate id`);
    }

    if (question.id) {
      ids.add(question.id);
    }

    if (!question.question) {
      errors.push(`${label}: missing question text`);
    }

    if (!question.type) {
      errors.push(`${label}: missing type`);
    }

    if (
      !Array.isArray(question.options) ||
      !question.options.length
    ) {
      errors.push(`${label}: no options`);
    }
  });

  const removedQuestionIds = [
    "sun_exposure",
    "spf_consistency",
    "routine_commitment",
    "treatment_interest",
    "treatment_fit",
    "event_timing"
  ];

  removedQuestionIds.forEach((id) => {
    if (ids.has(id)) {
      errors.push(
        `Removed question unexpectedly present: ${id}`
      );
    }
  });

  const report = {
    questionCount: questions.length,
    errors,
    valid: errors.length === 0
  };

  if (window.RISE_DEBUG) {
    console.group(
      "RISE SKIN CONCIERGE — QUIZ VALIDATION"
    );
    console.log(
      `Questions loaded: ${report.questionCount}`
    );
    console.log(
      "Currently visible questions:",
      window.RISE_SKIN
        .getVisibleQuizQuestions()
        .map((question) => question.id)
    );

    if (report.valid) {
      console.log(
        "Streamlined quiz validation passed."
      );
    } else {
      console.error("Quiz errors:", errors);
    }

    console.groupEnd();
  }

  return report;
};


if (window.RISE_DEBUG) {
  window.RISE_SKIN.validateQuiz();
}
